import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Cpu, LineChart, BellRing } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Container, Reveal } from "@/components/Reveal";
import { MARKET_NEWS, MARKET_SERIES } from "@/data/content";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function Chart({ title, unit, data, color }) {
  const id = `grad-${color.replace("#", "")}`;
  return (
    <div className="border border-line bg-white p-6 md:p-8">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
            Illustrative Index
          </p>
          <h3 className="mt-2 font-heading text-xl font-medium text-navy">{title}</h3>
        </div>
        <span className="font-mono text-xs text-slatey">{unit}</span>
      </div>
      <div className="mt-6 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
            <defs>
              <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="m"
              tick={{ fontSize: 11, fill: "#64748B" }}
              tickLine={false}
              axisLine={{ stroke: "#E2E8F0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#64748B" }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 2,
                border: "1px solid #E2E8F0",
                fontSize: 12,
                fontFamily: "JetBrains Mono, monospace",
              }}
            />
            <Area
              type="monotone"
              dataKey="v"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${id})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const tools = [
  { icon: LineChart, title: "Live Forward Curves", desc: "Real-time electricity and natural gas forward pricing across major hubs." },
  { icon: BellRing, title: "Renewal Alerts", desc: "Automated notifications as your contract windows and market conditions align." },
  { icon: Cpu, title: "Procurement Analytics", desc: "Interval-data analytics and scenario modeling built into your dashboard." },
];

export default function MarketCenter() {
  return (
    <>
      <SEO
        title="Market Center"
        description="Commercial energy market news, trends, and the intelligence tools Innervation Energy Group is building for smarter procurement timing."
        path="/market-center"
      />
      <PageHero
        overline="Market Center"
        titleLines={["Energy markets,", "in perspective."]}
        subtitle="Trends, commentary, and future market-intelligence tools — so you can time procurement decisions with confidence."
        crumbs={[{ label: "Market Center", path: "/market-center" }]}
      />

      <section className="bg-ice py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Market Snapshot</p>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            Illustrative seasonal price patterns.
          </h2>
          <p className="mt-4 max-w-2xl text-slatey">
            Sample data shown for educational purposes. Live market data and analytics
            tools are part of our forthcoming intelligence platform.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Reveal>
              <Chart title="Electricity" unit="$/MWh" data={MARKET_SERIES.electricity} color="#0052FF" />
            </Reveal>
            <Reveal delay={0.1}>
              <Chart title="Natural Gas" unit="$/MMBtu" data={MARKET_SERIES.gas} color="#00C853" />
            </Reveal>
          </div>
        </Container>
      </section>

      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="overline text-electric">Market Commentary</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
                What we're watching.
              </h2>
              <p className="mt-5 leading-relaxed text-slatey">
                Concise, commercial-focused takes on the developments shaping energy
                costs — from weather and storage to capacity auctions and regulation.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border-t border-line">
                {MARKET_NEWS.map((n, i) => (
                  <Reveal key={i}>
                    <div className="border-b border-line py-7">
                      <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-electric">
                        <TrendingUp className="h-3.5 w-3.5" />
                        {n.tag}
                        <span className="text-slatey">{fmtDate(n.date)}</span>
                      </div>
                      <h3 className="mt-3 font-heading text-xl font-medium text-navy">
                        {n.title}
                      </h3>
                      <p className="mt-2 leading-relaxed text-slatey">{n.excerpt}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-navy-dark py-24 text-white md:py-28">
        <Container>
          <p className="overline text-energy">Coming Soon</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight md:text-4xl">
            Energy intelligence tools.
          </h2>
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-3">
            {tools.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="h-full bg-navy-dark p-8">
                    <div className="flex h-12 w-12 items-center justify-center border border-white/15 text-energy">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-6 font-heading text-xl font-medium">{t.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/65">
                      {t.desc}
                    </p>
                    <span className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.16em] text-white/40">
                      In development
                    </span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      <CTABand
        heading="Want market context for your next decision?"
        text="Our advisors translate market movements into concrete procurement guidance for your facilities."
        secondary={{ label: "Education Center", to: "/education" }}
      />
    </>
  );
}
